import * as htmlToImage from 'html-to-image';
import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import {Animated, Platform, StyleSheet, View} from 'react-native';
import {PanGestureHandler, State} from 'react-native-gesture-handler';
import Svg, {Path} from 'react-native-svg';

import {Ecolors} from 'constant';
import {getUuid} from 'utils';

type Point = {
  x: number;
  y: number;
};

type Lines = Record<string, Point[]>;

function DrawLine(p: {setIsVisible: (t: boolean) => void}, ref: any) {
  const currentPathId = useRef<string | null>(null);
  const pan = useRef(new Animated.ValueXY()).current;

  const [stateLine, setStateLine] = useState<Lines>({});

  useImperativeHandle(ref, () => ({
    async accept(callback: (t: string) => void) {
      if (Platform.OS !== 'web') return;

      const node = document.getElementById('signature-view');
      if (!node) return;

      const dataUrl = await htmlToImage.toPng(node);

      callback(dataUrl);
    },

    clear() {
      setStateLine({});
      currentPathId.current = null;
    },
  }));

  const onGestureEvent = Animated.event(
    [
      {
        nativeEvent: {
          x: pan.x,
          y: pan.y,
        },
      },
    ],
    {
      useNativeDriver: false,
      listener: (event: any) => {
        if (!currentPathId.current) return;

        const {x, y} = event.nativeEvent;
        const id = currentPathId.current;

        setStateLine(prev => ({
          ...prev,
          [id]: [...(prev[id] || []), {x, y}],
        }));
      },
    },
  );

  const onHandlerStateChange = (event: any) => {
    const {state, x, y} = event.nativeEvent;

    if (state === State.BEGAN) {
      const id = getUuid();
      currentPathId.current = id;

      setStateLine(prev => ({
        ...prev,
        [id]: [{x, y}],
      }));
    }

    if (
      state === State.END ||
      state === State.CANCELLED ||
      state === State.FAILED
    ) {
      currentPathId.current = null;
    }
  };

  useEffect(() => {
    p.setIsVisible(Object.keys(stateLine).length > 0);
  }, [stateLine]);

  return (
    <PanGestureHandler
      onGestureEvent={onGestureEvent}
      onHandlerStateChange={onHandlerStateChange}>
      <View
        id="signature-view"
        nativeID="signature-view"
        style={styles.container}>
        <Svg width="100%" height="100%">
          {Object.entries(stateLine).map(([id, points]) => {
            if (points.length === 0) return null;

            const d =
              `M${points[0].x} ${points[0].y} ` +
              points
                .slice(1)
                .map(p => `L${p.x} ${p.y}`)
                .join(' ');

            return (
              <Path
                key={id}
                d={d}
                fill="none"
                stroke={Ecolors.textColor}
                strokeWidth={2}
              />
            );
          })}
        </Svg>
      </View>
    </PanGestureHandler>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Ecolors.whiteColor,
  },
});

export default React.forwardRef(DrawLine);
